// Hover-triggered panel rendered via createPortal so it is never clipped by overflow/stacking.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_Z = 4000;

import type { PortaledHoverPanelProps } from './types/SharedProps';

export type { PortaledHoverPanelProps };

export function PortaledHoverPanel(props: PortaledHoverPanelProps): JSX.Element {
  const {
    children,
    panel,
    panelWidthPx,
    panelClassName,
    gapPx = 6,
    zIndex = DEFAULT_Z,
  } = props;

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const left = Math.max(
      margin,
      Math.min(rect.left, window.innerWidth - panelWidthPx - margin),
    );
    // Provisional: below the anchor. Flipped/clamped once the panel's real
    // height is known (see layout effect below) — its content is dynamic
    // (schema guide text), so height can't be predicted up front.
    const top = rect.bottom + gapPx;
    setPos({ top, left });
  }, [panelWidthPx, gapPx]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  // Once the panel has actually rendered (and whenever its position/content
  // may have changed size), clamp it to the viewport vertically: flip above
  // the anchor if there isn't room below, or clamp to the bottom edge if it
  // wouldn't fit either way. Without this a panel opened near the bottom of
  // the screen (e.g. inside an already height-capped dropdown) renders
  // partly or entirely below the fold with no way to scroll to it.
  useLayoutEffect(() => {
    if (!open || !pos) return;
    const anchorEl = anchorRef.current;
    const panelEl = panelRef.current;
    if (!anchorEl || !panelEl) return;

    const margin = 8;
    const anchorRect = anchorEl.getBoundingClientRect();
    const panelHeight = panelEl.getBoundingClientRect().height;
    const fitsBelow = pos.top + panelHeight <= window.innerHeight - margin;
    if (fitsBelow) return;

    const spaceAbove = anchorRect.top - gapPx;
    const nextTop =
      spaceAbove - panelHeight >= margin
        ? spaceAbove - panelHeight
        : Math.max(margin, window.innerHeight - margin - panelHeight);

    if (Math.abs(nextTop - pos.top) > 0.5) {
      // Converges: once nextTop matches pos.top (within tolerance) this no
      // longer calls setPos, so depending on the full `pos` object here does
      // not loop — it just re-checks after each reposition until settled.
      setPos((prev) => (prev ? { ...prev, top: nextTop } : prev));
    }
  }, [open, pos, gapPx]);

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex"
      onMouseEnter={() => {
        setOpen(true);
        updatePosition();
      }}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              data-portaled-hover-panel="true"
              className={panelClassName}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex,
              }}
            >
              {panel}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
